/** @jsxImportSource @emotion/react */
import { CollapseComponent } from "../../../../components/CollapseComponent";
import { Collapse } from "antd";
import { DetailsRow } from "../../../../components/DetailsRow";
import { css } from "@emotion/react";
import { SpinnerBlock } from "../../../common/Spinner";
import { Placeholder } from "../../../../components/Placeholder";
import { UserMemberCardResponseData } from "../../../../service/api-response/UsersMemberCardsSuccessResponse";
import { UserMembercardsHeader } from "./UserMembercardsHeader";
import {
  useDeleteUserMembercard,
  useSyncUserMembercardStatus,
} from "../../../../service/api";
import { useParams } from "react-router-dom";
import { SmallButton } from "../../../../components/Buttons";
import { colors } from "../../../../components/colors";
import {
  UserMemberCardDeletePayload,
  UserMemberCardSyncPayload,
} from "../../../../service/api-response/UserMemberCardSyncCardSuccessResponse";
import { toastr } from "react-redux-toastr";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useSelector } from "react-redux";
import * as authSelectors from "../../../auth/authSelectors";
import { getErrorMessage } from "../../../../error-handler";

const panelStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
`;

const buttonWrapperStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

type Props = {
  membercards: UserMemberCardResponseData[] | undefined;
  isLoading: boolean;
};

export const UserMembercardsList = ({ membercards, isLoading }: Props) => {
  const { id } = useParams<{
    id: string;
  }>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const {
    mutateAsync: deleteMembercard,
    isLoading: deletionIsLoading,
  } = useDeleteUserMembercard(id);

  const {
    mutateAsync: syncMembercard,
    isLoading: syncIsLoading,
  } = useSyncUserMembercardStatus(id);

  const deleteHandler = async (id: string) => {
    const request: UserMemberCardDeletePayload = {
      cardId: id,
      organizationKeyName,
    };
    try {
      await deleteMembercard(request);
      toastr.success("Success", "Member card succesfully deleted from user");
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  const syncHandler = async (id: string) => {
    const request: UserMemberCardSyncPayload = {
      cardId: id,
      organizationKeyName,
    };
    try {
      const status = await syncMembercard(request);
      let msg = "";
      switch (status) {
        case "activated":
          msg = "Card synced and now is active";
          break;
        case "deleted":
          msg = "Card synced and now is deleted";
          break;
        case "not_changed":
          msg = `Card don't need syncing, current state is equal to POS state`;
          break;
        default:
          msg = `API returned unsupported sync action result: "${status}"`;
      }
      toastr.success("Success", msg);
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  if (isLoading) {
    return <SpinnerBlock />;
  }

  if (!membercards || membercards.length === 0) {
    return <Placeholder>User does not have any member cards</Placeholder>;
  }

  return (
    <CollapseComponent>
      {membercards.map((mc, index) => (
        <Collapse.Panel
          header={<UserMembercardsHeader membercard={mc} />}
          key={index}
        >
          <div css={panelStyle}>
            <DetailsRow leftText="Id" rightText={mc.id} />
            <DetailsRow leftText="Card number" rightText={mc.cardNumber} />
            <DetailsRow leftText="Tagline" rightText={mc.tagLine} />
            <div css={buttonWrapperStyle}>
              <SmallButton
                backgroundColor={colors.ctaMain}
                hoverColor={colors.ctaSecondaryOne}
                color={colors.white}
                onClick={async () => await syncHandler(mc.id)}
              >
                {syncIsLoading ? (
                  <FontAwesomeIcon icon={solid("spinner")} spin pulse />
                ) : (
                  "Sync"
                )}
              </SmallButton>
              {mc.isActive && (
                <SmallButton
                  backgroundColor={colors.errorDark}
                  hoverColor={colors.errorMedium}
                  color={colors.white}
                  onClick={async () => await deleteHandler(mc.id)}
                >
                  {deletionIsLoading ? (
                    <FontAwesomeIcon icon={solid("spinner")} spin pulse />
                  ) : (
                    "Delete"
                  )}
                </SmallButton>
              )}
            </div>
          </div>
        </Collapse.Panel>
      ))}
    </CollapseComponent>
  );
};
